/**
 * Standard events for every Hitz game. The list starts small and grows as games need it.
 * Game-specific events are declared in config/game.json > customEvents.
 * Names follow the platform's event map (2026-11): `object_action`, past tense, no "bet"; the site variant uses
 * underscores where the backend uses dots (`deposit.confirmed` ↔ `deposit_confirmed`). Keeping it from day one avoids a
 * rename when an event is registered in Hitz's telemetry.
 */
export const CORE_EVENTS = [
  'session_started',   // game loaded and received the session
  'level_started',     // { level }
  'level_completed',   // { level, score, durationMs }
  'level_failed',      // { level, score, durationMs, reason? }
  'score_updated',     // { value } — partial score relevant to platform rules
  'progress_reached',  // { step } — FTUE and meta milestones (e.g. "tutorial_done")
  'game_paused',
  'game_resumed',
  'error_occurred',    // { message } — gameplay error (does not replace game.error on the wire)
  'session_ended'      // { durationMs }
] as const

export type CoreEvent = (typeof CORE_EVENTS)[number]

export const MAX_EVENT_BYTES = 2048
const NAME_RE = /^[a-z][a-z0-9_]{1,47}$/

export class EventValidationError extends Error {}

export function validateEvent(name: string, data: unknown, customEvents: readonly string[]): void {
  if (!NAME_RE.test(name)) throw new EventValidationError(`invalid event name: "${name}"`)
  if (!(CORE_EVENTS as readonly string[]).includes(name) && !customEvents.includes(name)) {
    throw new EventValidationError(`event "${name}" is neither a core event nor listed in customEvents`)
  }
  if (data === undefined) return
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    throw new EventValidationError(`data for "${name}" must be an object`)
  }
  let json: string
  try { json = JSON.stringify(data) } catch { throw new EventValidationError(`data for "${name}" is not serializable`) }
  if (new TextEncoder().encode(json).length > MAX_EVENT_BYTES) {
    throw new EventValidationError(`data for "${name}" exceeds ${MAX_EVENT_BYTES} bytes`)
  }
}
